import { Button, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { getBridgeCollectionUrl } from '../../../config/ApiRouting';
import type { BridgeTenant } from '../../../infrastructure/BridgeTenantClient';

interface Props {
  tenant: BridgeTenant;
  onClose: () => void;
}

/**
 * The real output of registering a brand: the two values the merchant types into the Shopify theme
 * editor. Copy buttons are not a convenience — a hand-typed trailing slash turns every event into a
 * silent 404 that neither side can see.
 */
export default function BrandHandoffDialog({ tenant, onClose }: Props) {
  const handoffValues: Array<{ label: string; value: string }> = [
    { label: 'API base URL', value: getBridgeCollectionUrl() },
    { label: 'Tenant ID', value: tenant.tenantId },
  ];

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} 복사됨`);
    } catch {
      toast.error('복사에 실패했습니다. 직접 선택해 복사해 주세요.');
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">{tenant.brandName} 등록 완료</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          아래 두 값을 브랜드에 전달한다. Shopify 테마 에디터 → 앱 embed “SkinSeoul Bridge tracking”에 넣는 값이다.
        </p>

        {handoffValues.map(({ label, value }) => (
          <div key={label} className="d-flex align-items-center gap-2 border rounded p-2 bg-light mb-2">
            <span className="text-muted small" style={{ width: 96 }}>
              {label}
            </span>
            <span className="font-monospace small flex-grow-1 text-break">{value}</span>
            <Button size="sm" variant="outline-secondary" onClick={() => copy(label, value)}>
              복사
            </Button>
          </div>
        ))}

        <ol className="text-muted small mt-3 mb-0 ps-3">
          <li className="mb-1">스토어에 앱 설치</li>
          <li className="mb-1">
            테마 에디터 → <strong className="text-body">앱 embed</strong>에서 “SkinSeoul Bridge tracking” 켜기
          </li>
          <li className="mb-1">
            위 두 값 붙여넣기 <strong className="text-body">(URL 끝 슬래시 금지)</strong>
          </li>
          <li>브릿지 페이지 한 번 열기</li>
        </ol>

        <div className="alert alert-warning py-2 px-3 small mt-3 mb-0">
          <strong>URL 뒤에 슬래시를 붙이면 조용히 실패한다.</strong> 주소가 <code>…/business//bridgeEvents</code>가 되어
          404가 나는데, 익스텐션이 실패를 삼키도록 만들어져 있어 브랜드 쪽엔 아무 표시도 안 뜬다.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="dark" onClick={onClose}>
          목록으로
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
